import { Bot, Globe2, Search } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

type ChannelKey = "seo" | "aeo" | "geo";
const CHANNELS = [
  { key: "seo", label: "SEO", icon: Search, tagline: "Search visibility, measured against your own baseline.", checks: ["Crawl access", "Search intent", "Useful content", "Internal links"], sources: ["Search Console clicks & impressions", "Organic landing pages", "Qualified enquiries", "Conversions with known attribution"] },
  { key: "aeo", label: "AEO", icon: Bot, tagline: "Accurate answers and a repeatable record of observed citations.", checks: ["Buyer questions", "Clear answers", "Checkable sources", "Citation log"], sources: ["Recorded prompts & dates", "Brand mentions vs linked citations", "Assistant & search-mode settings", "Actual referral visits"] },
  { key: "geo", label: "GEO", icon: Globe2, tagline: "Useful information for generative search — without citation guarantees.", checks: ["Business facts", "Product details", "Readable pages", "Source accuracy"], sources: ["AI Overview source URLs", "Generative answers in your market", "Product & service accuracy", "Qualified leads, measured separately"] },
] as const;

export function ChannelVisualizer() {
  const [active, setActive] = useState<ChannelKey>("seo");
  const channel = CHANNELS.find((c) => c.key === active) ?? CHANNELS[0];
  return (
    <div className="relative rounded-3xl border border-border bg-card/40 backdrop-blur-md p-5 lg:p-7 overflow-hidden">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Search disciplines">
        {CHANNELS.map((c) => {
          const Icon = c.icon;
          return <Button key={c.key} role="tab" aria-selected={active === c.key} aria-controls="channel-panel" id={`channel-${c.key}`} onClick={() => setActive(c.key)} variant={active === c.key ? "default" : "ghost"}><Icon size={14} />{c.label}</Button>;
        })}
      </div>
      <div id="channel-panel" role="tabpanel" aria-labelledby={`channel-${channel.key}`}>
        <p className="mt-5 text-sm text-muted-foreground">{channel.tagline}</p>
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden border border-border">
          {channel.checks.map((check) => <div key={check} className="bg-background p-5 lg:p-6"><h3 className="font-display text-lg text-primary">{check}</h3></div>)}
        </div>
        <h3 className="mt-6 text-xs uppercase tracking-[0.2em] text-muted-foreground">What to measure</h3>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2 text-sm">
          {channel.sources.map((source) => <li key={source} className="flex gap-2 text-muted-foreground"><span aria-hidden="true" className="mt-2 size-1 shrink-0 rounded-full bg-primary" />{source}</li>)}
        </ul>
      </div>
    </div>
  );
}
